import type {
    SimulationStateNameTO,
    SimulationSummaryTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { SimulationStateNameTO as StateNames } from '@control-center/control-center-client-public-domain-simulation-ts';
import classnames from 'classnames';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import NoData from 'components/content/NoData';
import Tooltip from 'components/overlays/Tooltip';
import type React from 'react';

import SimulationStateBadge from './SimulationStateBadge';
import './_styles.scss';

interface ActionIconProps {
    icon: string;
    label: string;
    onClick: (e: React.MouseEvent) => void;
    danger?: boolean;
}

const ActionIcon = ({ icon, label, onClick, danger }: ActionIconProps) => (
    <Tooltip
        trigger={
            <button
                type='button'
                className={classnames('SimulationRow__actionBtn btn btn-icon btn-icon-only', {
                    'SimulationRow__actionBtn--danger': danger,
                })}
                onClick={onClick}
                data-testid={`domain-sim-action-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
                <span className={`rioglyph ${icon}`} />
            </button>
        }
        placement='top'
    >
        <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
);

interface RowActionsProps {
    simulation: SimulationSummaryTO;
    onStart: () => void;
    onDelete: () => void;
    onToggleConnectivity: () => void;
    onTriggerMrm: () => void;
}

const stopPropagation = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
};

const canToggleConnectivity = (state: SimulationStateNameTO) =>
    state !== StateNames.Completing && state !== StateNames.Completed;

const RowActions = ({ simulation, onStart, onDelete, onToggleConnectivity, onTriggerMrm }: RowActionsProps) => {
    const { state, isOnline } = simulation;

    return (
        <div className='SimulationRow__actions'>
            {state === StateNames.Idle && (
                <ActionIcon icon='rioglyph-play' label='Start' onClick={stopPropagation(onStart)} />
            )}
            {state === StateNames.Running && (
                <ActionIcon
                    icon='rioglyph-warning-sign'
                    label='Trigger MRM'
                    onClick={stopPropagation(onTriggerMrm)}
                    danger
                />
            )}
            {canToggleConnectivity(state) && (
                <ActionIcon
                    icon={isOnline ? 'rioglyph-wifi-off' : 'rioglyph-wifi'}
                    label={isOnline ? 'Go offline' : 'Go online'}
                    onClick={stopPropagation(onToggleConnectivity)}
                />
            )}
            <ActionIcon icon='rioglyph-trash' label='Delete' onClick={stopPropagation(onDelete)} danger />
        </div>
    );
};

interface Props {
    simulation: SimulationSummaryTO;
    scenarioNames: Map<string, string>;
    isSelected: boolean;
    onSelect: (vin: string) => void;
    onStart: (vin: string) => void;
    onDelete: (vin: string) => void;
    onToggleConnectivity: (vin: string) => void;
    onTriggerMrm: (vin: string) => void;
}

const SimulationRow = ({
    simulation,
    scenarioNames,
    isSelected,
    onSelect,
    onStart,
    onDelete,
    onToggleConnectivity,
    onTriggerMrm,
}: Props) => (
    <tr
        className={classnames('DomainSimulationRow', { 'DomainSimulationRow--active': isSelected })}
        data-testid='domain-sim-row'
        onClick={() => onSelect(simulation.vin)}
    >
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-vin'>
            {simulation.vin}
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-scenario'>
            {scenarioNames.get(simulation.scenarioId) ?? <NoData className='text-14' />}
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-state'>
            <SimulationStateBadge state={simulation.state} />
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-online'>
            {simulation.isOnline ? (
                <div className='DomainSimulationRow__onlineIndicator'>
                    <span className='rioglyph rioglyph-wifi text-primary' />
                    <span>Online</span>
                </div>
            ) : (
                <div className='DomainSimulationRow__onlineIndicator'>
                    <span className='rioglyph rioglyph-wifi-off' />
                    <span>Offline</span>
                </div>
            )}
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-missionId'>
            {simulation.missionId ?? <NoData className='text-14' />}
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-startedAt'>
            <DateTimeOrNoData value={simulation.startedAt ?? undefined} />
        </td>
        <td className='DomainSimulationRow__column' data-testid='domain-sim-row-createdAt'>
            <DateTimeOrNoData value={simulation.createdAt} />
        </td>
        <td
            className='DomainSimulationRow__column DomainSimulationRow__actionsColumn'
            data-testid='domain-sim-row-actions'
        >
            <RowActions
                simulation={simulation}
                onStart={() => onStart(simulation.vin)}
                onDelete={() => onDelete(simulation.vin)}
                onToggleConnectivity={() => onToggleConnectivity(simulation.vin)}
                onTriggerMrm={() => onTriggerMrm(simulation.vin)}
            />
        </td>
    </tr>
);

export default SimulationRow;
